import React, { useEffect, useCallback, useReducer, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Box, Typography, IconButton, Grid, Checkbox, Backdrop } from '@mui/material';
import { DateUtils } from '../../helpers/dateUtils';
import CompanyAutocomplete from '../common/CompanyAutocomplete';
import { MedicalJournalShortDto, MedicalJournalStatus, MedicalJournalStatusUtil } from '../../models/medicalCheckups/medicalJournalShortDto';
import MedicalJournalStatusSelect from '../common/MedicalJournalStatusSelect';
import { medicalJournalsService } from '../../services/medicalJournalsService';
import { medicalJournalSigningService } from '../../services/medicalJournalSigningService';
import { medicsService } from '../../services/medicsService';
import { CreateJournalDialog } from './CreateJournalDialog';
import { CompanyShortDto } from '../../models/companies/companyShortDto';
import ErrorAlert from '../common/ErrorAlert';
import Spinner from '../common/Spinner';
import { DatePickerFilter } from '../common/DatePickerFilter';
import TmPlus from '../../icons/TmPlus';
import TmPencil from '../../icons/TmPencil';
import { BulkSignConfirmationDialog } from './BulkSignConfirmationDialog';
import { JournalActionsMenu } from './JournalActionsMenu';
import { ShowForRole } from '../common/permissions/ShowForRole';
import { RoleType } from '../../models/enums/roleType';
import { filterStorageService } from '../../services/filterStorageService';
import { FilterSection } from '../../models/enums/filterSection';
import { TokenType } from '../../models/enums/tokenType';
import { permissionService } from '../../services/permissionService';
import { WorkplaceNotReadyDialog } from '../common/WorkplaceNotReadyDialog';
import { ValidationErrorDialog } from '../common/ValidationErrorDialog';
import { cryptoProService } from '../../services/cryptoProService';
import { ValidationError, WorkplaceError, NetworkError } from '../../services/medicalCheckupSigningService';

const TABLE_REFRESH_INTERVAL = 10000;

const initialState = {
    data: [] as MedicalJournalShortDto[],
    page: 0,
    rowsPerPage: 15,
    dataCount: 0,
    loading: false,
    loadingText: '',
    error: '',
    openCreateDialog: false,
    selectedJournals: [] as number[],
    openBulkSignDialog: false,
    workplaceError: '' as string,
    networkError: '' as string,
    validationError: '' as string,
    certificateType: null as TokenType | null,
    filter: {
        createdDate: null as Date | null,
        company: null as CompanyShortDto | null,
        status: 'all' as 'all' | MedicalJournalStatus,
    },
};

type State = typeof initialState;

type Action =
    | { type: 'SET_DATA'; payload: { data: MedicalJournalShortDto[]; dataCount: number } }
    | { type: 'SET_PAGE'; payload: number }
    | { type: 'SET_ROWS_PER_PAGE'; payload: number }
    | { type: 'SET_FILTER'; payload: Partial<State['filter']> }
    | { type: 'SET_LOADING'; payload: { loading: boolean; loadingText?: string } }
    | { type: 'SET_ERROR'; payload: string }
    | { type: 'TOGGLE_CREATE_DIALOG' }
    | { type: 'SET_SELECTED_JOURNALS'; payload: number[] }
    | { type: 'TOGGLE_BULK_SIGN_DIALOG' }
    | { type: 'SET_WORKPLACE_ERROR'; payload: string }
    | { type: 'SET_NETWORK_ERROR'; payload: string }
    | { type: 'SET_VALIDATION_ERROR'; payload: string }
    | { type: 'SET_CERTIFICATE_TYPE'; payload: TokenType | null };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_DATA':
            return { ...state, data: action.payload.data, dataCount: action.payload.dataCount };
        case 'SET_PAGE':
            return { ...state, page: action.payload };
        case 'SET_ROWS_PER_PAGE':
            return { ...state, rowsPerPage: action.payload, page: 0 };
        case 'SET_FILTER':
            return { ...state, filter: { ...state.filter, ...action.payload } };
        case 'SET_LOADING':
            return { ...state, loading: action.payload.loading, loadingText: action.payload.loadingText || '' };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'TOGGLE_CREATE_DIALOG':
            return { ...state, openCreateDialog: !state.openCreateDialog };
        case 'SET_SELECTED_JOURNALS':
            return { ...state, selectedJournals: action.payload };
        case 'TOGGLE_BULK_SIGN_DIALOG':
            return { ...state, openBulkSignDialog: !state.openBulkSignDialog };
        case 'SET_WORKPLACE_ERROR':
            return { ...state, workplaceError: action.payload };
        case 'SET_NETWORK_ERROR':
            return { ...state, networkError: action.payload };
        case 'SET_VALIDATION_ERROR':
            return { ...state, validationError: action.payload };
        case 'SET_CERTIFICATE_TYPE':
            return { ...state, certificateType: action.payload };
        default:
            return state;
    }
};

export const MedicalJournalTable = () => {
    const [state, dispatch] = useReducer(reducer, initialState, (baseState) => ({
        ...baseState,
        filter: filterStorageService.loadFilter(FilterSection.MedicalJournal, baseState.filter),
    }));
    const [workplaceCheckLoading, setWorkplaceCheckLoading] = useState(false);

    const refreshData = useCallback(() => {
        medicalJournalsService.getByFilter({
            createdDate: state.filter.createdDate,
            companyId: state.filter.company ? state.filter.company.id : null,
            statuses: state.filter.status === 'all' ? [] : [state.filter.status as MedicalJournalStatus],
            page: state.page,
            pageSize: state.rowsPerPage,
        })
            .then(r => {
                dispatch({ type: 'SET_DATA', payload: { data: r.data.data.items, dataCount: r.data.data.count } });
            })
            .catch(console.log);
    }, [state.filter, state.page, state.rowsPerPage]);

    useEffect(() => {
        refreshData();
        const interval = setInterval(refreshData, TABLE_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [refreshData]);

    // Проверяем тип сертификата медика при загрузке
    useEffect(() => {
        const checkCertificateType = async () => {
            if (!permissionService.hasRole(RoleType.Medic)) {
                return;
            }

            try {
                const defaultCert = await medicsService.getMedicCertificate();
                dispatch({ type: 'SET_CERTIFICATE_TYPE', payload: defaultCert?.tokenType ?? null });
            } catch (e: any) {
                console.error('Ошибка при проверке сертификата:', e);
                dispatch({ type: 'SET_CERTIFICATE_TYPE', payload: null });
            }
        };

        checkCertificateType();
    }, []);

    useEffect(() => {
        filterStorageService.saveFilter(FilterSection.MedicalJournal, state.filter);
    }, [state.filter]);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
    };

    const filterChanged = (name: string, value: any) => {
        dispatch({ type: 'SET_FILTER', payload: { [name]: value } });
    };

    const handleDownload = async (journalId: number) => {
        dispatch({ type: 'SET_LOADING', payload: { loading: true, loadingText: 'Подготовка файла для скачивания...' } });
        medicalJournalsService.getDownloadLink(journalId)
            .then(r => {
                const url = r.data.data.link;
                window.location.href = url;
            })
            .catch(e => showAlert(e.response.data.message ?? e.message))
            .finally(() => {
                dispatch({ type: 'SET_LOADING', payload: { loading: false, loadingText: '' } });
            });
    };

    const handleDownloadPdf = async (journalId: number) => {
        dispatch({ type: 'SET_LOADING', payload: { loading: true, loadingText: 'Подготовка PDF файла для скачивания...' } });
        try {
            const response = await medicalJournalsService.downloadPdf(journalId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `medical-journal-${journalId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e: any) {
            showAlert(e.response?.data?.message ?? "Ошибка при скачивании PDF файла");
        } finally {
            dispatch({ type: 'SET_LOADING', payload: { loading: false, loadingText: '' } });
        }
    };

    const showAlert = (errorMessage: string) => {
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
    }

    function renderStatus(journal: MedicalJournalShortDto) {
        const strValue = MedicalJournalStatusUtil.getStringValue(journal.status);
        let color = '#ec4a71';
        let backgroundColor = '#fce4ea';

        switch (journal.status) {
            case MedicalJournalStatus.Signed:
                color = '#5bba64';
                backgroundColor = '#edfbee';
                break;
            case MedicalJournalStatus.Signing:
                color = '#ff9800';
                backgroundColor = '#fff3e0';
                break;
            case MedicalJournalStatus.SigningError:
                color = '#f44336';
                backgroundColor = '#ffebee';
                break;
            default:
                color = '#ec4a71';
                backgroundColor = '#fce4ea';
        }

        return <Box display="inline-block" color={color} sx={{ backgroundColor: { backgroundColor }, borderRadius: "25px", paddingLeft: "15px", paddingRight: "15px" }}>{strValue}</Box>;
    };

    const onCloseCreateJournalDialog = () => {
        dispatch({ type: 'TOGGLE_CREATE_DIALOG' });
        refreshData();
    }

    // Bulk selection methods
    const handleSelectAll = () => {
        const signableJournals = state.data.filter(journal =>
            journal.status === MedicalJournalStatus.Unsigned || journal.status === MedicalJournalStatus.SigningError
        );
        dispatch({ type: 'SET_SELECTED_JOURNALS', payload: signableJournals.map(journal => journal.id) });
    };

    const handleDeselectAll = () => {
        dispatch({ type: 'SET_SELECTED_JOURNALS', payload: [] });
    };

    const handleSelectJournal = (journalId: number) => {
        const journal = state.data.find(j => j.id === journalId);

        if (journal && journal.status !== MedicalJournalStatus.Unsigned && journal.status !== MedicalJournalStatus.SigningError) {
            return; // Don't allow selection of non-signable journals
        }

        const newSelected = state.selectedJournals.includes(journalId)
            ? state.selectedJournals.filter(id => id !== journalId)
            : [...state.selectedJournals, journalId];
        dispatch({ type: 'SET_SELECTED_JOURNALS', payload: newSelected });
    };

    const handleBulkSign = async () => {
        if (state.selectedJournals.length === 0) return;

        dispatch({ type: 'SET_LOADING', payload: { loading: true, loadingText: 'Подписание журналов...' } });
        try {
            await medicalJournalSigningService.signMedicalJournals(
                state.selectedJournals,
                {
                    onProgress: (progress) => {
                        dispatch({ type: 'SET_LOADING', payload: { loading: true, loadingText: progress.message } });
                    }
                }
            );
            dispatch({ type: 'SET_SELECTED_JOURNALS', payload: [] });
            refreshData();
        } catch (e: any) {
            console.error(e);
            
            if (e instanceof ValidationError) {
                dispatch({ type: 'SET_VALIDATION_ERROR', payload: e.message });
            } else if (e instanceof NetworkError) {
                dispatch({ type: 'SET_NETWORK_ERROR', payload: e.message });
            } else if (e instanceof WorkplaceError) {
                dispatch({ type: 'SET_WORKPLACE_ERROR', payload: e.message });
            } else {
                if (e.response) {
                    const errorMessage = e.response?.data?.message ?? e.message ?? "Ошибка валидации";
                    dispatch({ type: 'SET_VALIDATION_ERROR', payload: errorMessage });
                } else {
                    const errorMessage = e.message ?? "Ошибка при подписании журналов";
                    dispatch({ type: 'SET_WORKPLACE_ERROR', payload: errorMessage });
                }
            }
        } finally {
            dispatch({ type: 'TOGGLE_BULK_SIGN_DIALOG' });
            dispatch({ type: 'SET_LOADING', payload: { loading: false, loadingText: '' } });
        }
    };

    const handleSingleSign = (journalId: number) => {
        dispatch({ type: 'SET_SELECTED_JOURNALS', payload: [journalId] });
        dispatch({ type: 'TOGGLE_BULK_SIGN_DIALOG' });
    };

    const isAllSelected = () => {
        const signableJournals = state.data.filter(journal =>
            journal.status === MedicalJournalStatus.Unsigned || journal.status === MedicalJournalStatus.SigningError
        );
        return signableJournals.length > 0 && state.selectedJournals.length === signableJournals.length;
    };

    const isPartiallySelected = () => {
        const signableJournals = state.data.filter(journal =>
            journal.status === MedicalJournalStatus.Unsigned || journal.status === MedicalJournalStatus.SigningError
        );
        return state.selectedJournals.length > 0 && state.selectedJournals.length < signableJournals.length;
    };

    return (
        <Box >
            {state.validationError && (
                <ValidationErrorDialog
                    open
                    message={state.validationError}
                    onClose={() => dispatch({ type: 'SET_VALIDATION_ERROR', payload: '' })}
                />
            )}
            {state.networkError && (
                <ValidationErrorDialog
                    open
                    title="Ошибка связи"
                    message={state.networkError}
                    onClose={() => dispatch({ type: 'SET_NETWORK_ERROR', payload: '' })}
                />
            )}
            {state.workplaceError && (
                <WorkplaceNotReadyDialog
                    open
                    message={state.workplaceError}
                    loading={workplaceCheckLoading}
                    onClose={() => dispatch({ type: 'SET_WORKPLACE_ERROR', payload: '' })}
                    onRetry={async () => {
                        setWorkplaceCheckLoading(true);
                        try {
                            const wpStatus = await cryptoProService.checkWorkplaceReadiness();
                            if (wpStatus.isReady) {
                                dispatch({ type: 'SET_WORKPLACE_ERROR', payload: '' });
                            } else {
                                dispatch({
                                    type: 'SET_WORKPLACE_ERROR',
                                    payload: wpStatus.errors ?? 'Рабочее место не готово к подписанию',
                                });
                            }
                        } finally {
                            setWorkplaceCheckLoading(false);
                        }
                    }}
                />
            )}
            {state.error && (
                <ErrorAlert
                    message={state.error}
                    onClose={() => dispatch({ type: 'SET_ERROR', payload: '' })}
                    autoCloseDuration={10000} />
            )}
            <div>
                <Grid container pb={1} mt={2.5}>
                    <Grid item xs={10} style={{ display: 'flex', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center" marginBottom={1}>
                            <DatePickerFilter
                                label="Дата"
                                name="createdDate"
                                value={state.filter.createdDate}
                                onChange={(value, name) => filterChanged(name, value)}
                                style={{ width: 200, marginRight: 10, marginTop: 4 }}
                            />

                            <CompanyAutocomplete
                                company={state.filter.company}
                                onCompanyChange={(e) => filterChanged('company', e)}
                                style={{ width: 200, marginRight: 10 }}
                            />

                            <MedicalJournalStatusSelect
                                name="status"
                                value={state.filter.status}
                                onChange={(event) => filterChanged("status", event.target.value)}
                                includeAll={true}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={2} style={{ display: 'inline-block', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <ShowForRole role={RoleType.Medic}>
                                <IconButton
                                    sx={{
                                        display: "flex",
                                        '&.Mui-disabled': {
                                            opacity: 0.5,
                                            cursor: 'not-allowed'
                                        }
                                    }}
                                    className="list-item"
                                    onClick={() => dispatch({ type: 'TOGGLE_BULK_SIGN_DIALOG' })}
                                    disabled={state.selectedJournals.length === 0}
                                >
                                    <TmPencil fill="none" stroke="white" />&nbsp;
                                    <Typography pr={1}>
                                        <span style={{ fontWeight: 'bold' }}>Подписать</span>
                                        {state.selectedJournals.length > 0 ? ` (${state.selectedJournals.length})` : ''}
                                    </Typography>
                                </IconButton>
                            </ShowForRole>
                            <IconButton sx={{ display: "flex" }} className="list-item" onClick={() => dispatch({ type: 'TOGGLE_CREATE_DIALOG' })}>
                                <TmPlus fill="none" stroke="white" />&nbsp;
                                <Typography fontWeight="bold" pr={1}>
                                    Сформировать
                                </Typography>
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>

                <TableContainer sx={{ '.MuiTableCell-root': { py: 1.5 } }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <ShowForRole role={RoleType.Medic}>
                                    <TableCell>
                                        <Checkbox
                                            checked={isAllSelected()}
                                            indeterminate={isPartiallySelected()}
                                            onChange={isAllSelected() ? handleDeselectAll : handleSelectAll}
                                            sx={{
                                                color: '#9588e8',
                                                '&.Mui-checked': {
                                                    color: '#9588e8',
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: '#9588e8',
                                                }
                                            }}
                                        />
                                        <Typography variant="body1" sx={{ color: "#7c7e92", display: 'inline', ml: 1 }}>
                                            {isAllSelected() ? "Отменить выбор" : "Выбрать все"}
                                        </Typography>
                                    </TableCell>
                                </ShowForRole>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>ID</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Клиент</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Подписал</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Дата</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Сформировавший пользователь</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Статус</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Дата подписания</Typography></TableCell>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Действия</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.data.map((item) => (
                                <TableRow key={item.id}>
                                    <ShowForRole role={RoleType.Medic}>
                                        <TableCell>
                                            <Checkbox
                                                checked={state.selectedJournals.includes(item.id)}
                                                onChange={() => handleSelectJournal(item.id)}
                                                disabled={item.status !== MedicalJournalStatus.Unsigned && item.status !== MedicalJournalStatus.SigningError}
                                                sx={{
                                                    color: '#9588e8',
                                                    '&.Mui-checked': {
                                                        color: '#9588e8',
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: '#bdbdbd',
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    </ShowForRole>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.id}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.companyName}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.signedByFio}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{DateUtils.formatDateTime(item.createdDate)}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.createdByFio}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{renderStatus(item)}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        {item.signedDate ? DateUtils.formatDateTime(item.signedDate) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <JournalActionsMenu
                                            journal={item}
                                            onDownload={handleDownload}
                                            onDownloadPdf={handleDownloadPdf}
                                            onSign={handleSingleSign}
                                            hasMultipleSelections={state.selectedJournals.length > 1}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography>Всего: {state.dataCount}</Typography>
                    <TablePagination
                        rowsPerPageOptions={[15, 30, 50]}
                        component="div"
                        count={state.dataCount}
                        rowsPerPage={state.rowsPerPage}
                        page={state.page}
                        onPageChange={(event: any, page: number) => dispatch({ type: 'SET_PAGE', payload: page })}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={"Строк на странице:"}
                        labelDisplayedRows={({ from, to, count }: any): string => `Показано ${from}-${to} из ${count}`}
                    />
                </Box>
                <CreateJournalDialog
                    open={state.openCreateDialog}
                    onClose={onCloseCreateJournalDialog}
                />
                <BulkSignConfirmationDialog
                    open={state.openBulkSignDialog}
                    onClose={() => dispatch({ type: 'TOGGLE_BULK_SIGN_DIALOG' })}
                    onConfirm={handleBulkSign}
                    selectedCount={state.selectedJournals.length}
                />
            </div>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={state.loading}
            >
                <Spinner text={state.loadingText} />
            </Backdrop>
        </Box>
    );
};